import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Modal, renderShowConfirmationModal, onHideHandler } from '..';
import { Field } from '../Form';
import { useBeforeUnload } from '../../hooks/useBeforeUnload/useBeforeUnload';

const EditPhotoSeriesVehicleDetails = ({
  show,
  onModalClose,
  vin,
  registrationNo,
  externalId,
  showVin,
  showRegistrationNo,
  showExternalId,
  onSave,
  ...props
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useBeforeUnload(hasUnsavedChanges);

  const initialValues = {
    vin: vin || '',
    registrationNo: registrationNo || '',
    externalId: externalId || '',
  };

  const handleSubmit = (values, { setStatus }) => {
    const payload = {};
    if (showVin) payload.vin = values.vin;
    if (showRegistrationNo) payload.registrationNo = values.registrationNo;
    if (showExternalId) payload.externalId = values.externalId;
    onSave(
      payload,
      (message) => {
        setSuccessMessage(message);
        setHasUnsavedChanges(false);
      },
      (error) => {
        setStatus(error);
        setHasUnsavedChanges(false);
      },
    );
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, () => {
        onModalClose();
        setShowConfirmModal(false);
        setHasUnsavedChanges(false);
      })}
      <Modal
        show={show}
        size="md"
        onHide={() => onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal)}
        headerText="Edit vehicle details"
        backdrop="static"
        {...props}
      >
        <Modal.Body>
          {successMessage && (
            <div className="row">
              <div className="col">
                <Alert variant="success" dismissible onClick={() => setSuccessMessage(null)}>
                  {successMessage}
                </Alert>
              </div>
            </div>
          )}
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
              vin: Yup.string(),
              registrationNo: Yup.string(),
              externalId: Yup.string(),
            })}
            onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers)}
            validateOnChange={false}
          >
            {({ errors, status, setStatus, touched, isSubmitting }) => (
              <Form
                onChange={({ target }) => {
                  if (!hasUnsavedChanges && target.value !== initialValues[target.name]) {
                    setHasUnsavedChanges(true);
                  }
                }}
              >
                {status && (
                  <div className="row mb-2">
                    <div className="col">
                      <Alert variant="danger" dismissible onClick={() => setStatus(null)}>
                        {status}
                      </Alert>
                    </div>
                  </div>
                )}
                {showRegistrationNo && (
                  <div className="row mb-2">
                    <div className="col">
                      <Field
                        type="text"
                        name="registrationNo"
                        label="Registration No"
                        touched={touched.registrationNo}
                        errors={errors.registrationNo}
                      />
                    </div>
                  </div>
                )}
                {showVin && (
                  <div className="row mb-2">
                    <div className="col">
                      <Field
                        type="text"
                        name="vin"
                        label="VIN"
                        touched={touched.vin}
                        errors={errors.vin}
                      />
                    </div>
                  </div>
                )}
                {showExternalId && (
                  <div className="row mb-2">
                    <div className="col">
                      <Field
                        type="text"
                        name="externalId"
                        label="External ID"
                        touched={touched.externalId}
                        errors={errors.externalId}
                      />
                    </div>
                  </div>
                )}
                <div className="row mt-3">
                  <div className="col text-right">
                    <button type="button" className="btn btn-secondary mr-1" onClick={onModalClose} disabled={isSubmitting}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      Save
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

EditPhotoSeriesVehicleDetails.propTypes = {
  show: PropTypes.bool,
  onModalClose: PropTypes.func.isRequired,
  vin: PropTypes.string,
  registrationNo: PropTypes.string,
  externalId: PropTypes.string,
  showVin: PropTypes.bool,
  showRegistrationNo: PropTypes.bool,
  showExternalId: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

export default EditPhotoSeriesVehicleDetails;
